/**
 * Timing edits of the Song & Timing workspace (W2-08 timing; DESIGN.md §5.2): each one is a live, undoable
 * transaction running the shared core command bodies (`Core.tempo`, `Core.transform`) through `DocStore.transact`
 * (no Apply button). Failures (a command's `tx.fail` reason) and command notes become toasts; a keep-time edit
 * that re-timed records shows the re-timing report ("Re-timed 1,204 items, 3 snapped to 1/1920 beat"); tempo and
 * offset edits mark the session so the "other difficulties still use the old tempo map" notice can show.
 * Repeated small edits (arrow steps, Alt+wheel, offset nudges) share a coalesce key, so a burst is one undo step.
 */

import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { TempoEditPolicy } from '../../core/commands/types';
import type { BpmId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { TxBuilder } from '../../core/ops/types';
import type { TxnMeta, TxnOutcome } from '../../state/types';
import { m } from './timing.messages';
import { beatText, viewOf } from './timingDoc';
import { meterWith, meterWithout, pointAtBeat, setOffsetArgFor, type TempoRow } from './timingModel';
import { currentPolicy, noteTimingEdit } from './timingSession';

type EditServices = Pick<AppServices, 'store' | 'ui' | 'editor' | 'persist'>;

interface RunOptions {
  /** Toast the command notes (default true). */
  readonly notes?: boolean;
  /** A tempo or offset edit (the other-difficulties notice). */
  readonly timing?: boolean;
}

/** Runs a transaction; toasts its failure or notes. Null when no project is open. */
export function runTx<R>(
  s: EditServices,
  meta: TxnMeta,
  build: (tx: TxBuilder) => R,
  opts: RunOptions = {},
): TxnOutcome<R> | null {
  const store = s.store;
  if (store === null) return null;
  const out = store.transact(meta, build);
  if (!out.ok) {
    s.ui.toast(out.reason, { kind: 'error' });
    return out;
  }
  if (opts.notes !== false) for (const n of out.notes) s.ui.toast(n, { kind: 'info' });
  if (opts.timing === true && out.txn !== null) noteTimingEdit(s);
  return out;
}

const ref = (key: MessageRef['key'], params?: MessageRef['params']): MessageRef =>
  params === undefined ? { key } : { key, params };

function retimeReport(s: EditServices, r: { readonly retimed: number; readonly snapped: number }): void {
  if (r.retimed > 0 || r.snapped > 0) {
    s.ui.toast(ref(m.retimed, { retimed: r.retimed, snapped: r.snapped }), { kind: 'info', undo: true });
  }
}

function policyOf(s: EditServices): TempoEditPolicy {
  return currentPolicy(s);
}

// ---------------------------------------------------------------------------------------------------------------
// Tempo
// ---------------------------------------------------------------------------------------------------------------

/** Sets the BPM of a point. `coalesce` merges a burst of steps into one undo entry. */
export function setPointBpm(s: EditServices, id: BpmId, bpm: number, coalesce?: string): boolean {
  const out = runTx(
    s,
    {
      label: ref(m.txBpm, { bpm }),
      command: 'timing.setBpm',
      ...(coalesce === undefined ? {} : { coalesceKey: `${coalesce}:${String(id)}` }),
    },
    (tx) => Core.tempo.setBpm(tx, { id, bpm, policy: policyOf(s) }),
    { notes: false, timing: true },
  );
  if (out?.ok !== true) return false;
  retimeReport(s, out.value);
  return true;
}

/** Sets the song tempo (the point at beat 0). */
export function setSongBpm(s: EditServices, bpm: number, coalesce?: string): boolean {
  const store = s.store;
  const first = store === null ? undefined : store.idx.bpmSorted()[0];
  if (first === undefined) return false;
  return setPointBpm(s, first, bpm, coalesce);
}

/** Multiplies every tempo point by `factor` in one transaction (octave fixes x2, /2, x1.5). */
export function multiplyTempo(s: EditServices, factor: number): boolean {
  const store = s.store;
  if (store === null || !(factor > 0) || !Number.isFinite(factor)) return false;
  const ids = [...store.idx.bpmSorted()];
  const first = store.doc.bpm.get(ids[0] as BpmId);
  if (first === undefined) return false;
  const policy = policyOf(s);
  const out = runTx(
    s,
    { label: ref(m.txBpm, { bpm: Math.round(first.bpm * factor * 100) / 100 }), command: 'timing.multiply' },
    (tx) => {
      let retimed = 0;
      let snapped = 0;
      // Later points first: with keep-time, earlier changes move later points, so walk from the end.
      for (const id of [...ids].reverse()) {
        const p = tx.doc.bpm.get(id);
        if (p === undefined) continue;
        const r = Core.tempo.setBpm(tx, { id, bpm: Math.round(p.bpm * factor * 10000) / 10000, policy });
        retimed += r.retimed;
        snapped += r.snapped;
      }
      return { retimed, snapped };
    },
    { notes: false, timing: true },
  );
  if (out?.ok !== true) return false;
  retimeReport(s, out.value);
  return true;
}

/** Adds a tempo change at a beat with the tempo in effect there (or `withBpm`). */
export function addTempoAt(s: EditServices, t: Beat, withBpm?: number): boolean {
  const store = s.store;
  if (store === null) return false;
  const bpm = withBpm ?? pointAtBeat(viewOf(store).points, t)?.bpm ?? 120;
  const out = runTx(
    s,
    { label: ref(m.txAddBpm, { beat: beatText(t) }), command: 'timing.addTempo' },
    (tx) => Core.tempo.addBpm(tx, { t, bpm, policy: policyOf(s) }),
    { notes: false, timing: true },
  );
  if (out?.ok !== true) return false;
  retimeReport(s, out.value);
  return true;
}

/** Moves a table row (its tempo change and its meter change) to another beat; the beat-0 row cannot move. */
export function moveRow(s: EditServices, row: TempoRow, t: Beat): boolean {
  if (row.pinned || Beats.eq(row.t, t)) return false;
  const policy = policyOf(s);
  const out = runTx(
    s,
    {
      label: ref(row.point !== null ? m.txMoveBpm : m.txMeter, { beat: beatText(t), beats: row.beatsPerBar }),
      command: 'timing.moveRow',
    },
    (tx) => {
      let r = { retimed: 0, snapped: 0 };
      if (row.point !== null) r = Core.tempo.setBpm(tx, { id: row.point.id, t, policy });
      if (row.mark !== null) {
        Core.tempo.setMeter(tx, {
          marks: meterWith(meterWithout(tx.doc.timing.meter, row.t), t, row.mark.beatsPerBar),
        });
      }
      return r;
    },
    { notes: false, timing: row.point !== null },
  );
  if (out?.ok !== true) return false;
  retimeReport(s, out.value);
  return true;
}

/** Deletes a table row: its tempo change and its meter change (one transaction; the beat-0 row cannot go). */
export function deleteRow(s: EditServices, row: TempoRow): boolean {
  if (row.pinned) return false;
  const store = s.store;
  if (store === null) return false;
  const policy = policyOf(s);
  const out = runTx(
    s,
    {
      label: ref(row.point !== null ? m.txDeleteBpm : m.txDeleteMeter, { beat: beatText(row.t) }),
      command: 'timing.deleteRow',
    },
    (tx) => {
      let r = { retimed: 0, snapped: 0 };
      if (row.point !== null) r = Core.tempo.deleteBpm(tx, { id: row.point.id, policy });
      if (row.mark !== null) Core.tempo.setMeter(tx, { marks: meterWithout(tx.doc.timing.meter, row.t) });
      return r;
    },
    { notes: false, timing: row.point !== null },
  );
  if (out?.ok !== true) return false;
  retimeReport(s, out.value);
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Meter
// ---------------------------------------------------------------------------------------------------------------

/** Sets the beats per bar from beat `t` on (adds the meter change when there is none at `t`). */
export function setMeterAt(s: EditServices, t: Beat, beatsPerBar: number): boolean {
  const store = s.store;
  if (store === null) return false;
  const out = runTx(
    s,
    { label: ref(m.txMeter, { beats: beatsPerBar, beat: beatText(t) }), command: 'timing.setMeter' },
    (tx) => {
      Core.tempo.setMeter(tx, { marks: meterWith(tx.doc.timing.meter, t, beatsPerBar) });
    },
  );
  return out?.ok === true;
}

/** Adds a meter change at a beat (keeping the meter in force there). False when one is already there. */
export function addMeterAt(s: EditServices, t: Beat): boolean {
  const store = s.store;
  if (store === null) return false;
  const meter = store.doc.timing.meter;
  if (meter.some((mk) => Beats.eq(mk.t, t))) {
    s.ui.toast(ref(m.meterExists, { beat: beatText(t) }), { kind: 'warning' });
    return false;
  }
  const perBar = viewOf(store).meterMap.beatsPerBarAt(t);
  const out = runTx(s, { label: ref(m.txAddMeter, { beat: beatText(t) }), command: 'timing.addMeter' }, (tx) => {
    Core.tempo.setMeter(tx, { marks: meterWith(tx.doc.timing.meter, t, perBar) });
  });
  return out?.ok === true;
}

// ---------------------------------------------------------------------------------------------------------------
// Offset and confirmation
// ---------------------------------------------------------------------------------------------------------------

/** Makes Phira hear beat 0 at `heardMs` (META.offset + info.yml offset). */
export function setHeardOffset(s: EditServices, heardMs: number, coalesce?: string): boolean {
  const store = s.store;
  if (store === null || !Number.isFinite(heardMs)) return false;
  const arg = setOffsetArgFor(heardMs, store.doc.pkg);
  const out = runTx(
    s,
    {
      label: ref(m.txOffset, { sec: heardMs / 1000 }),
      command: 'timing.setOffset',
      ...(coalesce === undefined ? {} : { coalesceKey: coalesce }),
    },
    (tx) => {
      Core.tempo.setOffset(tx, { ms: arg });
    },
    { notes: false, timing: true },
  );
  return out?.ok === true;
}

/** Moves beat 0 by `deltaMs` (nudges and by-ear answers; bursts coalesce). */
export function nudgeOffset(s: EditServices, deltaMs: number): boolean {
  const store = s.store;
  if (store === null || !Number.isFinite(deltaMs) || deltaMs === 0) return false;
  return setHeardOffset(s, viewOf(store).heardMs + deltaMs, 'timing.offset');
}

/** Folds the imported per-chart adjustment into META.offset. */
export function foldPerChart(s: EditServices): boolean {
  const out = runTx(s, { label: ref(m.txFold), command: 'timing.foldInfoOffset' }, (tx) => {
    Core.tempo.foldInfoOffset(tx, {});
  });
  if (out?.ok !== true) return false;
  s.ui.toast(ref(m.folded), { kind: 'success', undo: true });
  return true;
}

export function setConfirmed(s: EditServices, confirmed: boolean): boolean {
  const store = s.store;
  if (store === null || store.doc.timing.confirmed === confirmed) return false;
  const out = runTx(s, { label: ref(confirmed ? m.txConfirm : m.txUnconfirm), command: 'timing.confirm' }, (tx) => {
    Core.tempo.setTimingConfirmed(tx, { confirmed });
  });
  return out?.ok === true;
}

// ---------------------------------------------------------------------------------------------------------------
// Insert / remove time
// ---------------------------------------------------------------------------------------------------------------

export function insertBeats(s: EditServices, at: Beat, length: Beat): boolean {
  const out = runTx(
    s,
    { label: ref(m.txInsert, { beats: beatText(length) }), command: 'timing.insertBeats' },
    (tx) => Core.transform.insertTime(tx, { at, length, scope: 'all' }),
    { timing: true },
  );
  if (out?.ok !== true) return false;
  s.ui.toast(ref(m.inserted, { beats: beatText(length), count: out.value.moved }), { kind: 'success', undo: true });
  return true;
}

export function removeRange(s: EditServices, from: Beat, to: Beat): boolean {
  if (!Beats.lt(from, to)) {
    s.ui.toast(ref(m.rangeInvalid), { kind: 'error' });
    return false;
  }
  const out = runTx(
    s,
    { label: ref(m.txRemove, { from: beatText(from), to: beatText(to) }), command: 'timing.removeRange' },
    (tx) => Core.transform.removeTime(tx, { from, to, scope: 'all' }),
    { timing: true },
  );
  if (out?.ok !== true) return false;
  s.ui.toast(ref(m.removed, { removed: out.value.removed, moved: out.value.moved }), { kind: 'success', undo: true });
  return true;
}
